import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  User, Mail, Phone, Key, Shield, Copy, Eye, EyeOff,
  RefreshCw, CheckCircle2, Building2, Percent, Info, Loader2, Save,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as engineService from "@/shared/api/services/engineService";

/* ─── Section Card ───────────────────────────────────────────── */
function SectionCard({ title, description, icon: Icon, accent = "#22d3ee", children, delay = 0 }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 160, damping: 18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative"
    >
      <motion.div
        animate={{ opacity: hovered ? 1 : 0 }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 0%, ${accent}0d, transparent 70%)`, filter: "blur(6px)" }}
      />
      <motion.div
        animate={{ y: hovered ? -3 : 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 28 }}
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
          border: `1px solid ${hovered ? `${accent}25` : "rgba(148,163,184,0.09)"}`,
          boxShadow: hovered ? `0 14px 40px rgba(0,0,0,0.4), 0 0 24px ${accent}10` : "0 4px 18px rgba(0,0,0,0.25)",
          transition: "border-color 0.3s, box-shadow 0.3s",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}40, transparent)` }} />
        <motion.div
          animate={{ x: hovered ? "108%" : "-108%" }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        {/* Header */}
        <div className="flex items-center gap-3 p-5 sm:p-6" style={{ borderBottom: "1px solid rgba(148,163,184,0.07)" }}>
          <motion.div
            animate={{ scale: hovered ? 1.08 : 1, rotate: hovered ? 5 : 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${accent}30, ${accent}10)`,
              border: `1px solid ${accent}25`,
              boxShadow: hovered ? `0 0 18px ${accent}30` : "none",
              transition: "box-shadow 0.3s",
            }}
          >
            <Icon className="h-5 w-5" style={{ color: accent }} />
          </motion.div>
          <div>
            <p className="text-sm font-bold text-slate-100">{title}</p>
            {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
          </div>
        </div>
        <div className="p-5 sm:p-6">{children}</div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Styled Input ───────────────────────────────────────────── */
function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function StyledInput({ icon: Icon, value, onChange, type = "text", disabled, placeholder, accent = "#22d3ee" }) {
  const [focused, setFocused] = useState(false);
  return (
    <div className="relative">
      {Icon && (
        <motion.div
          animate={{ color: focused ? accent : "#475569" }}
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        >
          <Icon className="h-4 w-4" />
        </motion.div>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        style={{
          width: "100%", padding: `9px ${Icon ? "12px 9px 38px" : "12px"}`,
          paddingLeft: Icon ? 38 : 12,
          borderRadius: 10, fontSize: 13,
          background: disabled ? "rgba(2,6,23,0.5)" : "rgba(2,6,23,0.7)",
          border: `1px solid ${focused ? `${accent}35` : "rgba(148,163,184,0.1)"}`,
          color: disabled ? "#475569" : "#e2e8f0",
          outline: "none",
          boxShadow: focused ? `0 0 0 3px ${accent}0d` : "none",
          transition: "border-color 0.2s, box-shadow 0.2s",
          opacity: disabled ? 0.6 : 1,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function EngineSettings() {
  const queryClient = useQueryClient();
  const resellerName = "Gaming Partners Ltd";
  const { t } = useLanguage();

  const { data: engineProfile, isLoading: profileLoading, isFetching } = useQuery({
    queryKey: ["engine-profile"],
    queryFn: () => engineService.getProfile(),
    refetchInterval: 5000,
    staleTime: 0,
  });

  const [reseller, setReseller] = useState({
    name: "",
    code: "",
    email: "",
    contact_name: "",
    support_email: "",
    contact_phone: "",
    notes: "",
    api_key: "",
    secret_key: "",
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [regenerateDialog, setRegenerateDialog] = useState(false);
  const [copied, setCopied] = useState(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (!engineProfile) return;
    setReseller({
      name: engineProfile.name || "",
      code: engineProfile.code || "",
      email: engineProfile.email || "",
      contact_name: engineProfile.contactName || "",
      support_email: engineProfile.supportEmail || "",
      contact_phone: engineProfile.contactPhone || "",
      notes: engineProfile.notes || "",
      api_key: engineProfile.apiKey || "",
      secret_key: engineProfile.secretKey || "",
    });
  }, [engineProfile]);

  const updateMutation = useMutation({
    mutationFn: (input) => engineService.updateProfile(input),
    onSuccess: (updated) => {
      setSaveError("");
      queryClient.setQueryData(["engine-profile"], updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (error) => {
      const message = String(error?.message || "Failed to save changes.").replace(/^Network error:\s*/i, "");
      setSaveError(message);
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => engineService.regenerateApiKeys(),
    onSuccess: (updated) => {
      queryClient.setQueryData(["engine-profile"], (prev) => ({ ...(prev || {}), ...(updated || {}) }));
      setReseller((prev) => ({
        ...prev,
        api_key: updated?.apiKey || prev.api_key,
        secret_key: updated?.secretKey || prev.secret_key,
      }));
      setRegenerateDialog(false);
    },
  });

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleRegenerate = () => regenerateMutation.mutate();

  const handleSave = async () => {
    const trimmedSupportEmail = reseller.support_email.trim();

    updateMutation.mutate({
      name: reseller.name.trim(),
      contactName: reseller.contact_name.trim(),
      contactPhone: reseller.contact_phone.trim(),
      supportEmail: trimmedSupportEmail || undefined,
      notes: reseller.notes.trim(),
    });
  };

  const KeyRow = ({ label, keyVal, show, onToggleShow, copyType }) => (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <div className="flex gap-2">
        <div className="flex-1 px-3 py-2.5 rounded-xl font-mono text-xs overflow-hidden text-ellipsis whitespace-nowrap text-cyan-400"
          style={{ background: "rgba(2,6,23,0.8)", border: "1px solid rgba(34,211,238,0.12)" }}>
          {show ? keyVal : "••••••••••••••••••••••••••••••••••••••"}
        </div>
        {[
          { icon: show ? EyeOff : Eye, fn: onToggleShow, label: "toggle" },
          { icon: copied === copyType ? CheckCircle2 : Copy, fn: () => copyToClipboard(keyVal, copyType), label: "copy" },
        ].map((btn, i) => (
          <motion.button
            key={i}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.93 }}
            onClick={btn.fn}
            className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer outline-none flex-shrink-0"
            style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)", transition: "background 0.18s" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.12)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(34,211,238,0.06)"}
          >
            <btn.icon className="h-3.5 w-3.5" style={{ color: copied === copyType && btn.label === "copy" ? "#34d399" : "#64748b" }} />
          </motion.button>
        ))}
      </div>
    </div>
  );

  return (
    <EngineLayout currentPage="EngineSettings" resellerName={resellerName}>
      <div className="space-y-6 max-w-3xl">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden p-5 sm:p-6"
          style={{
            background: "linear-gradient(135deg, rgba(15,23,42,0.85), rgba(8,14,30,0.9))",
            border: "1px solid rgba(139,92,246,0.15)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.45), rgba(34,211,238,0.3), transparent)" }} />
          <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none" style={{ background: "radial-gradient(circle at 100% 0%, rgba(139,92,246,0.07), transparent 70%)" }} />
          <div className="relative">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-violet-400 mb-1">Configuration</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-1">{t("settings")}</h1>
            <p className="text-sm text-slate-400">{t("manageProfile") || "Manage your reseller profile & API credentials"}</p>
            <p className="text-xs text-slate-500 mt-2">{isFetching ? "Syncing..." : "Live sync every 5s"}</p>
          </div>
        </motion.div>

        {/* Profile Card */}
        <SectionCard title={t("engineProfile") || "Engine Profile"} description={t("yourBusinessInfo") || "Your business information"} icon={Building2} accent="#22d3ee" delay={0.05}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("engineName") || "Engine Name"}>
                <StyledInput value={reseller.name} onChange={e => setReseller(p => ({ ...p, name: e.target.value }))} />
              </Field>
              <Field label={t("engineCode") || "Engine Code"}>
                <StyledInput value={reseller.code} disabled />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("email") || "Email"}>
                <StyledInput icon={Mail} type="email" value={reseller.email} disabled />
              </Field>
              <Field label={t("contactName") || "Contact Name"}>
                <StyledInput icon={User} value={reseller.contact_name} onChange={e => setReseller(p => ({ ...p, contact_name: e.target.value }))} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("supportEmail") || "Support Email"}>
                <StyledInput icon={Mail} type="email" value={reseller.support_email} onChange={e => setReseller(p => ({ ...p, support_email: e.target.value }))} />
              </Field>
              <Field label={t("contactPhone") || "Contact Phone"}>
                <StyledInput icon={Phone} value={reseller.contact_phone} onChange={e => setReseller(p => ({ ...p, contact_phone: e.target.value }))} />
              </Field>
            </div>
            <Field label={t("notes") || "Notes"} full>
              <StyledInput icon={Info} value={reseller.notes} onChange={e => setReseller(p => ({ ...p, notes: e.target.value }))} />
            </Field>
            <div className="pt-2">
              <motion.button
                whileHover={{ scale: 1.03, y: -1 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleSave}
                disabled={profileLoading || updateMutation.isPending}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer outline-none border-0"
                style={{
                  background: saved ? "linear-gradient(135deg, #34d399, #059669)" : "linear-gradient(135deg, #22d3ee, #0284c7)",
                  boxShadow: saved ? "0 4px 20px rgba(52,211,153,0.38)" : "0 4px 20px rgba(34,211,238,0.38)",
                  transition: "background 0.3s, box-shadow 0.3s",
                  opacity: (profileLoading || updateMutation.isPending) ? 0.7 : 1,
                }}
              >
                {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <CheckCircle2 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                {updateMutation.isPending ? (t("saving") || "Saving…") : saved ? "Saved!" : (t("saveChanges") || "Save Changes")}
              </motion.button>
              {saveError && (
                <p className="text-xs text-rose-400 mt-2">{saveError}</p>
              )}
            </div>
          </div>
        </SectionCard>

        {/* API Keys Card */}
        <SectionCard title={t("apiCredentialsTitle") || "API Credentials"} description={t("yourApiKeysIntegration") || "Your keys for integration"} icon={Key} accent="#a78bfa" delay={0.1}>
          <div className="space-y-5">
            {/* Security Notice */}
            <div className="flex gap-3 p-4 rounded-xl" style={{ background: "rgba(251,146,60,0.06)", border: "1px solid rgba(251,146,60,0.2)" }}>
              <Shield className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-orange-300 mb-1">{t("securityNotice") || "Security Notice"}</p>
                <p className="text-xs text-orange-400/70">{t("keepSecretConfidential") || "Keep your secret key confidential."} {t("neverExposeSecretKey") || "Never expose it publicly."}</p>
              </div>
            </div>

            <KeyRow
              label={t("apiKeyPublic") || "Public API Key"}
              keyVal={reseller.api_key}
              show={showApiKey}
              onToggleShow={() => setShowApiKey(v => !v)}
              copyType="api"
            />
            <KeyRow
              label={t("secretKey") || "Secret Key"}
              keyVal={reseller.secret_key}
              show={showSecretKey}
              onToggleShow={() => setShowSecretKey(v => !v)}
              copyType="secret"
            />
          </div>
        </SectionCard>

        {/* Fees & Commission */}
        {engineProfile && (
          <SectionCard title={t("feesAndCommission") || "Fees & Commission"} description={t("feesAndCommissionDesc") || "Your commission rates"} icon={Percent} accent="#34d399" delay={0.15}>
            <div className="space-y-4">
              {engineProfile.parentName && (
                <div className="flex gap-3 p-4 rounded-xl" style={{ background: "rgba(34,211,238,0.05)", border: "1px solid rgba(34,211,238,0.15)" }}>
                  <Info className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-cyan-300 mb-3">
                      {(t("givenBy") || "Given by {type} {name}").replace("{type}", engineProfile.parentType).replace("{name}", engineProfile.parentName)}
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: t("depositCommission") || "Deposit %", value: engineProfile.parentDepositCommission },
                        { label: t("withdrawalCommission") || "Withdrawal %", value: engineProfile.parentWithdrawalCommission },
                        { label: t("percentageFee") || "Fee %", value: engineProfile.parentPercentageFee },
                      ].map((item, i) => (
                        <div key={i} className="p-2.5 rounded-lg text-center" style={{ background: "rgba(2,6,23,0.5)", border: "1px solid rgba(34,211,238,0.1)" }}>
                          <p className="text-[10px] text-slate-500 mb-1">{item.label}</p>
                          <p className="text-lg font-bold text-cyan-400">
                            {item.value !== null && item.value !== undefined ? `${item.value}%` : "N/A"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: t("depositCommissionPercent") || "Deposit Commission %", value: engineProfile.depositCommission },
                  { label: t("withdrawalCommissionPercent") || "Withdrawal Commission %", value: engineProfile.withdrawalCommission },
                  { label: t("percentageFeePercent") || "Percentage Fee %", value: engineProfile.percentageFee },
                ].map((item, i) => (
                  <div key={i}>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{item.label}</label>
                    <StyledInput
                      value={item.value !== null && item.value !== undefined ? item.value : "N/A"}
                      disabled
                      accent="#34d399"
                    />
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>
        )}

        {/* Danger Zone */}
        <SectionCard title={t("regenerateApiKeysTitle") || "Regenerate API Keys"} description={t("regenerateApiKeysDesc") || "This will invalidate your existing keys"} icon={RefreshCw} accent="#f87171" delay={0.2}>
          <motion.button
            whileHover={{ scale: 1.03, y: -1 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setRegenerateDialog(true)}
            disabled={regenerateMutation.isPending}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer outline-none border-0"
            style={{
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              boxShadow: "0 4px 20px rgba(239,68,68,0.3)",
              opacity: regenerateMutation.isPending ? 0.7 : 1,
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 32px rgba(239,68,68,0.45)"}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(239,68,68,0.3)"}
          >
            <RefreshCw className={`h-4 w-4 ${regenerateMutation.isPending ? "animate-spin" : ""}`} />
            {t("regenerateAllKeys") || "Regenerate All Keys"}
          </motion.button>
        </SectionCard>
      </div>

      {/* Regenerate Dialog */}
      <AlertDialog open={regenerateDialog} onOpenChange={setRegenerateDialog}>
        <AlertDialogContent style={{ background: "rgba(2,6,23,0.98)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 16 }}>
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(248,113,113,0.4), transparent)" }} />
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">{t("regenerateApiKeys") || "Regenerate API Keys"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">{t("regenerateApiKeysConfirm") || "This action cannot be undone. All existing integrations using these keys will stop working."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.14)", color: "#94a3b8", borderRadius: 10 }}>{t("cancel") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerate} style={{ background: "linear-gradient(135deg, #ef4444, #dc2626)", borderRadius: 10, border: "none", fontWeight: 700, color: "#fff", cursor: "pointer" }}>
              {t("regenerateKeys") || "Regenerate Keys"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </EngineLayout>
  );
}